/** As the monitoring-event API sends it. */
export enum MonitoringEventStatus {
  TO_REVIEW = "toReview",
  CONFIRMED = "confirmed",
  NOT_CORRECT_DIFFERENT_ENTITY = "notCorrectDifferentEntity",
  NOT_CORRECT_NO_LONGER_APPLIES = "notCorrectNoLongerApplies",
  NOT_CORRECT_OTHER_REASON = "notCorrectOtherReason",
  PREVIOUS_RESULTS_STILL_APPLY = "previousResultsStillApply",
  CORRECT = "correct",
  NOT_CORRECT = "notCorrect",
  /** Set by the system when a newer event supersedes this one before anyone reviews it. */
  NOT_REVIEWED = "notReviewed"
}

/** There is no such assignee in the API, so the list asks for the "Not reviewed" status instead. */
export const MONITORING_NO_ASSIGNEE_POSSIBLE_FILTER = "noAssigneePossible";

export const MONITORING_ASSIGNEE_OPTIONS_MAX_LIMIT = 100;

/** The Customer filter searches the API's customer options, which needs this many characters and returns at most `MAX_LIMIT`. */
export const MONITORING_CUSTOMER_SEARCH_MIN_LENGTH = 2;
export const MONITORING_CUSTOMER_OPTIONS_MAX_LIMIT = 50;

/**
 * Filter types of the Monitoring page's "Additional filters". `ALL` searches the customer's name and its labels at once;
 * `LABELS` is the labels filter of `customer/filter-options`.
 */
export enum MonitoringFilterKey {
  ALL = "all",
  CUSTOMER = "customer",
  LABELS = "labels"
}

export const MONITORING_SORT_KEY_ORDER_MAX_KEYS = 200;

/** How a date travels in the list request and the page URL. */
export const MONITORING_DATE_PARAM_FORMAT = "YYYY-MM-DD";

export const MONITORING_REVIEW_COMMENT_MAX_LENGTH = 5000;

/** Refuses a review written against an event a newer one has already replaced. */
export const MONITORING_SUPERSEDED_ERROR_CODE = "MONITORING_REVIEW_NOT_NEWEST_EVENT";

export enum MonitoringSortField {
  CREATED_AT = "createdAt",
  CUSTOMER_NAME = "customerName",
  SECTION = "section",
  CHANGE = "change",
  STATUS = "status",
  ASSIGNEE_NAME = "assigneeName"
}

export enum MonitoringSourceAccuracy {
  ACCURATE = "accurate",
  INACCURATE = "inaccurate",
  UNMARKED = "unmarked"
}

export enum MonitoringRewriteStatus {
  PENDING = "pending",
  DONE = "done",
  SKIPPED = "skipped",
  FAILED = "failed"
}

/** How a listed entry matched the customer's name: `matches` or `possible_matches` of the screen. */
export enum MonitoringListMatchType {
  EXACT = "exact",
  POSSIBLE = "possible"
}

/** A sanctions / mandate list's name, by the event's `warningCategory`: the same keys the list cards use. */
export const MONITORING_LIST_TITLE_KEYS: Readonly<Record<string, string>> = {
  nationalSanctions: "sanctions-and-mandates.National_sanctions_list",
  europeanSanctions: "sanctions-and-mandates.European_sanctions_list",
  europeanTravelBan: "sanctions-and-mandates.European_travel_ban_list",
  unSanctions: "sanctions-and-mandates.UN_sanctions_list",
  ofacSdnSanctions: "sanctions-and-mandates.OFAC_SDN_sanctions_list",
  ofacConsolidatedSanctions: "sanctions-and-mandates.OFAC_consolidated_sanctions_list",
  politicalMandatePdf: "political-involvements.Rekenhof",
  politicalMandateCsv: "political-involvements.Flemish_Mandate_Database"
};

/** What an earlier review said about one listed entry. */
export enum MonitoringListVerdict {
  MATCHED = "matched",
  REJECTED = "rejected"
}

export enum MonitoringEventKind {
  WARNING_RESULT = "warningResult",
  WARNING_REMOVED = "warningRemoved",
  SIMPLE = "simple"
}

export enum MonitoringPanelMode {
  REVIEW = "review",
  RE_REVIEW = "reReview"
}

/** Not a status the API sends: the review form before anything is picked. */
export enum ReviewOutcomeState {
  UNSET = "unset"
}

/** The two status groups, as the translation keys spell them. */
export enum ReviewOutcomeGroup {
  CORRECT = "correct",
  NOT_CORRECT = "not_correct"
}

export enum MonitoringSectionTab {
  ADVERSE_MEDIA = "adverseMedia",
  SANCTIONS = "sanctions",
  POLITICAL_INVOLVEMENTS = "politicalInvolvements",
  KYC = "companyDetails"
}

export enum KycSubTab {
  PROFILE = "profile",
  MANDATES = "connections",
  PARTICIPATIONS = "participations",
  SAME_ADDRESS = "cohabitation",
  LOCATIONS = "locations"
}
